import * as actions from "../../mutation-types";
import check from "../../../domains2/libraries/check-types-ext";
import ShopsProvider from "../../../providers/ShopsProvider";
import PaymentProvider from "../../../providers/PaymentProvider";
import OrdersProvider from "../../../providers/OrdersProvider";
import PickupDateTimeRulesProvider from "../../../providers/PickupDateTimeRulesProvider";
import AppError from "../../../domains2/libraries/AppError";
import _ from "lodash";

import appConfig from "../../../appconfig/index";
import {generateEvaluateTags} from "../../../domains2/helpers/targetGroups";
import TargetGroupsProvider from "../../../providers/TargetGroupsProvider";
import {DeliveryMethodsEnum} from "../../../domains2/libraries/DeliveryMethods";
import {evaluateCustomerType, getSessionKey, shouldApplyCustomAsapTime} from "../../../libraries/utils";
import {computeTags, getCustomerType} from "../../../helper/SubscriptionHelper";
import GrantPointsToNonUser from "../../../businessRules/pendingOrder/grantPointsToNonLoggedInSunsetPointUser";
import HelloRetail from "../../../integrations/recommendations/helloRetail/HelloRetail";
import {verifyVoucherAvailability} from "../../../helper/VoucherValidationHelper";

const TimeBoxFormats = appConfig.timeBoxImpl.Formats;

function getAccumulatedQuantities(currentProductSelections) {
	const productQuantityMap = {};
	for (const item of currentProductSelections) {
		if (productQuantityMap[item.productId]) {
			productQuantityMap[item.productId] = productQuantityMap[item.productId] + item.quantity;
		} else {
			productQuantityMap[item.productId] = item.quantity;
		}
	}
	return productQuantityMap;
}

function appendReorderData(orderDetails, getters) {
	orderDetails.orderOptions = {...orderDetails.orderOptions, isReorder: true};
	const currentProductsQuantitiesMap = getAccumulatedQuantities(getters.selections);
	const originalProductsQuantitiesMap = getAccumulatedQuantities(getters.originalProductSelectionsFromPreviousOrder);
	const reorderQuantityDifference = Object.keys(currentProductsQuantitiesMap).reduce((acc, productId) => {
		if (originalProductsQuantitiesMap[productId] && currentProductsQuantitiesMap[productId] > originalProductsQuantitiesMap[productId] || !originalProductsQuantitiesMap[productId]) {
			acc += currentProductsQuantitiesMap[productId] - (originalProductsQuantitiesMap[productId] || 0);
		}
		return acc;
	}, 0);
	orderDetails.orderOptions = {
		...orderDetails.orderOptions,
		reorderQuantityDifference,
		reorderValueDifference: (Math.round((getters.totalPrice - getters.originalPreviousOrderTotal) * 100)) / 100
	};
}
// actions
export default {
	[actions.ADD_PRODUCT_SELECTION_TO_CART]: ({ commit, getters }, productSelection) => {
		const productSelections = Array.isArray(productSelection) ? productSelection : [productSelection];
		productSelections.forEach(ps => {
			check.object.orFail(ps);
		});

		commit(actions.ADD_TO_CART, {
			productSelection: productSelections,
			maxLeadDay: getters.maxLeadDay
		});

		commit(actions.CHECKOUT_RESET);
		setLastUpdateTime(commit);
	},
	[actions.ADD_TO_CART_SINGLE_ITEM]: ({ commit, getters }, productSelection) => {
		commit(actions.ADD_TO_CART, {
			productSelection: [productSelection],
			maxLeadDay: getters.maxLeadDay
		});
		setLastUpdateTime(commit);
	},
	[actions.CHECKOUT_RESET_WITH_LAST_UPDATE_TIME]: ({ commit }) => {
		commit(actions.CHECKOUT_RESET);
		setLastUpdateTime(commit);
	},
	[actions.REMOVE_FROM_CART]: ({ commit }, id) => {
		const ids = Array.isArray(id) ? id : [id];

		ids.forEach(pid => {
			commit(actions.REMOVE_FROM_CART, {
				id: pid
			});
		});

		// when product is removed, checkout step is reset to step one
		commit(actions.CHECKOUT_RESET);
		setLastUpdateTime(commit);
	},
	[actions.UPDATE_CART_PRODUCT_SELECTIONS_BY_PRODUCT_ID]: ({commit}, {productId, productSelections}) => {
		// first remove all product selections for this product

		commit(actions.REMOVE_FROM_CART, {
			id: productId
		});

		// then add bulk new product selections
		commit(actions.ADD_TO_CART_BULK, {
			productSelections
		});
	},

	[actions.REMOVE_FROM_CART_BY_PRODUCT_SELECTION_ID]: ({ commit }, productSelectionId) => {
		commit(actions.REMOVE_FROM_CART_BY_PRODUCT_SELECTION_ID, {
			productSelectionId
		});

		// when product is removed, checkout step is reset to step one
		commit(actions.CHECKOUT_RESET);
		setLastUpdateTime(commit);
	},

	[actions.REMOVE_FROM_CART_BULK]: ({ commit }, productSelections) => {
		commit(actions.REMOVE_FROM_CART_BULK, {
			productSelections
		});
	},

	[actions.REMOVE_FROM_CART_SUB_PRODUCT]: ({ commit }, product) => {
		commit(actions.REMOVE_FROM_CART_SUB_PRODUCT, {
			product
		});
	},

	[actions.REMOVE_FROM_CART_WITHOUT_CHECKOUT_RESET]: ({ commit }, productSelectionId) => {
		commit(actions.REMOVE_FROM_CART_WITHOUT_CHECKOUT_RESET, {
			productSelectionId
		});
	},

	/**
     * Use this action to empty the cart.
     * From a vue component this is invoked like this:
     * this.$store.dispatch(actions.CART_EMPTY).
     * @param commit
     * @returns {*}
     */
	[actions.CART_EMPTY_ALL]: ({ commit }) => {
		setLastUpdateTime(commit);
		return commit(actions.CART_EMPTY_ALL);
	},
	[actions.PURGE_CART]: ({ commit }) => {
		setLastUpdateTime(commit);
		return commit(actions.PURGE_CART);
	},
	[actions.UPDATE_CART_QUANTITY]: ({ commit }, { id, quantity }) => {
		return new Promise((resolve) => {
			commit(actions.UPDATE_CART_QUANTITY, {
				id: id,
				quantity: quantity
			});

			// when product quantity is updated, checkout step is reset to step one
			commit(actions.CHECKOUT_RESET);
			setLastUpdateTime(commit);
			resolve();
		});
	},

	[actions.UPDATE_CART_QUANTITY_BY_ID]: ({ commit }, { productSelectionId, quantity }) => {
		return new Promise((resolve) => {
			commit(actions.UPDATE_CART_QUANTITY_BY_ID, {
				productSelectionId: productSelectionId,
				quantity: quantity
			});

			// when product quantity is updated, checkout step is reset to step one
			commit(actions.CHECKOUT_RESET);
			setLastUpdateTime(commit);
			resolve();
		});
	},

	[actions.CART_SUBMIT_USER_DATA]: ({ commit, getters }) => {
		const featureToggles = getters.featureToggles;
		return new Promise((resolve) => {
			commit(actions.CART_SUBMIT_USER_DATA);
			if (!featureToggles.newCheckoutFlow) {
				commit(actions.CART_INCREASE_STEP, {
					currentStep: "stepUserDataWrapper"
				});
			}
			setLastUpdateTime(commit);
			resolve();
		});
	},

	[actions.CART_SET_SHOP_DATA]: ({ commit, getters }, shopData) => {
		const featureToggles = getters.featureToggles;
		return new Promise((resolve) => {
			commit(actions.CART_SET_SHOP_DATA, shopData);
			if (shopData.submitted) {
				if (!featureToggles.newCheckoutFlow) {
					commit(actions.CART_INCREASE_STEP, {
						currentStep: "stepShop"
					});
				}
			}
			resolve();
		});
	},

	[actions.CART_SET_PICKUP_DATA]: ({ commit, getters }, pickupData) => {
		const featureToggles = getters.featureToggles;
		return new Promise((resolve) => {
			commit(actions.CART_SET_PICKUP_DATA, pickupData);
			if (pickupData.submitted) {
				if (!featureToggles.newCheckoutFlow) {
					commit(actions.CART_INCREASE_STEP, {
						currentStep: "stepPickupData"
					});
				}
			}
			setLastUpdateTime(commit);
			resolve();
		});
	},
	[actions.ADD_TEMPORARY_ADDRESS_TO_PERMANENT_ADDRESS]: ({ commit }, deliveryDetailsObj) => {
		return commit(actions.ADD_TEMPORARY_ADDRESS_TO_PERMANENT_ADDRESS, deliveryDetailsObj);
	},
	[actions.ADD_DELIVERY_TIME]: ({ commit }, deliveryTime) => {
		return commit(actions.ADD_DELIVERY_TIME, deliveryTime);
	},
	[actions.ADD_DELIVERY_ADDRESS_AND_FLOOR]: ({ commit }, deliveryObject) => {
		return commit(actions.ADD_DELIVERY_ADDRESS_AND_FLOOR, deliveryObject);
	},
	[actions.ADD_TEMPORARY_ADDRESS]: ({ commit }, tempAddressObj) => {
		return commit(actions.ADD_TEMPORARY_ADDRESS, tempAddressObj);
	},
	[actions.CART_INITIALIZE_PAYMENT]: ({ state, commit, getters }, { urlAccept, urlCancel, urlCheckout, integrationType, meta = {} }) => {
		const TimeBoxHelper = getters.ShopTimeBoxHelper;

		const userLoggedIn = getters.applicationUserLoggedIn;
		const featureToggles = getters.featureToggles;

		const isEmployeeDiscount = appConfig.tenantId === 6 ? true : "employeeDiscount" in getters.tenantSpecificData;

		const employeeDiscountNumberObject = isEmployeeDiscount && getters.discount ? { employeeDiscountNumber: getters.discount.promoCode } : {};
		const employeeDiscountToken = getters.employeeDiscountToken && getters.employeeDiscountToken.token
			? getters.employeeDiscountToken
			: undefined;
		const sunsetPoints = getters.sunsetPoints;
		const userDetails = getters.getUserData;
		const deliveryChoice = state.persistedData.newCartData.tenantSpecificData.deliveryChoice;
		const deliveryDetails = deliveryChoice === DeliveryMethodsEnum.DELIVERY
			? getters.deliveryDetails || {}
			: {};
		const customerDetails = {
			name: userDetails.name,
			email: userDetails.email,
			telephone: userDetails.tel,
			address: deliveryDetails.address,
			doorFloor: deliveryDetails.doorFloor,
			comments: deliveryDetails.comments,
			emailNewsletter: userDetails.emailNewsletter,
			...(userDetails.userTimeZone ? { userTimeZone: userDetails.userTimeZone } : {}),
			...employeeDiscountNumberObject,
			employeeDiscountToken: employeeDiscountToken,
			customerType: evaluateCustomerType()
		};

		const shopId = (() => {
			// if shop has shop data in vuex take it from there
			const shopIdStore = _.get(state, "persistedData.newCartData.shopData.id");
			if (shopIdStore) {
				return shopIdStore;
			}

			// if shop has global data then take id from there
			const shopIdGlobal = _.get(getters, "globalSelectedShop.id");
			if (shopIdGlobal) {
				return shopIdGlobal;
			}

			// else throw error
			throw AppError.createNewError("ShopDataNotFound");
		})();

		const asapWaitingTime = getters.tenantSettings.asapWaitingTime;
		const customAsapTime = getters.tenantSettings.customAsapTime;
		const isCustomClosingTimeEnabled = getters.featureToggles.useCustomClosingTimeOffset;

		const applyCustomAsapTime = shouldApplyCustomAsapTime(getters, asapWaitingTime);

		const pickupDate = state.persistedData.newCartData.pickupTime === "ASAP" ?
			TimeBoxHelper.nowInTimeZone()
				.add({
					minutes: isCustomClosingTimeEnabled && applyCustomAsapTime ? customAsapTime : asapWaitingTime.minutes
				})
				.format(TimeBoxFormats.YYYY_MM_DD).exec() :
			state.persistedData.newCartData.pickupDate;
		const pickupTime = state.persistedData.newCartData.pickupTime === "ASAP" ?
			TimeBoxHelper.nowInTimeZone()
				.add({
					minutes: isCustomClosingTimeEnabled && applyCustomAsapTime ? customAsapTime : asapWaitingTime.minutes
				})
				.set({ second: asapWaitingTime.seconds })
				.format(TimeBoxFormats.HH_mm_ss).exec() :
			state.persistedData.newCartData.pickupTime;
		const orderDetails = {
			cartOrderId: state.persistedData.newCartData.cartOrderId,
			shopId: shopId,
			pickupDate: pickupDate,
			pickupTime: pickupTime,
			productSelections: state.persistedData.newCartData.productSelections,
			customerDetails: customerDetails,
			orderOptions: {
				mustBeHeated: state.persistedData.newCartData.mustBeHeated,
				paymentLinkThroughEmail: featureToggles.paymentLinkThroughEmail,
				...state.persistedData.newCartData.tenantSpecificData,
				sunsetPoints,
				enableProductBestPriceInCatalogAndCart: featureToggles.enableProductBestPriceInCatalogAndCart,
				...(getters.channelAlias === "kiosk" && { shopDeviceIdentifier: (window.kioskHost || {}).shopDeviceIdentifier})
			},
			specialNotes: state.persistedData.newCartData.specialNotes,
			userConsent: state.persistedData.newCartData.userConsent,
			isPrepaid: true,
			integrationType,
			prepaidUrlAccept: urlAccept,
			prepaidUrlCancel: urlCancel,
			prepaidUrlCheckout: urlCheckout,
			subscriptionEnabled: userLoggedIn && featureToggles.enableSubscription,
			inPreparation: meta ? meta.inPreparation : false,
			redeemedVouchers: state.persistedData.newCartData.vouchersRedeemed,
			orderRef: getSessionKey(appConfig.clientTraceId(), getters.deviceData),
			extendedData: {fcmToken: getters.featureToggles.postPurchaseDiscount && getters.fcmToken}
		};
		if (getters.qrCodeTableServingData.isQrCodeTableServing) {
			orderDetails.orderOptions = {...orderDetails.orderOptions, ...getters.qrCodeTableServingData};
		}
		if (getters.friendInviteCode) {
			orderDetails.orderOptions = {...orderDetails.orderOptions, friendInviteCode: getters.friendInviteCode};
		}
		if (getters.selectedDealId) {
			orderDetails.orderOptions = {...orderDetails.orderOptions, deals: getters.selectedDealId};
		}
		if (getters.ppdProductSelectionIds && getters.ppdProductSelectionIds.length) {
			orderDetails.orderOptions.ppdFlowSkipSendingPushNotification = true;
		}
		if(getters.isReorderState) {
			appendReorderData(orderDetails, getters);
		}
		if(getters.voucherVirtualOrderId) {
			orderDetails.orderOptions = {...orderDetails.orderOptions, voucherVirtualOrderId: getters.voucherVirtualOrderId};
		}

		const shop = getters.globalSelectedShop || {};
		const shopPaymentOptions = shop.shopPaymentOptions || {};
		const recurringPaymentDetails = getters.recurringPaymentDetails || {};
		const accounts = Object.keys(recurringPaymentDetails).filter(key => key !== "customerReference"); // if not stored for selected shop use first account to copy from here
		const savedPaymentMethod = recurringPaymentDetails[shopPaymentOptions.merchantBillwerkAccountId] || recurringPaymentDetails[accounts[0]];

		if (savedPaymentMethod) {
			orderDetails.recurringPaymentDetails = {
				customerReference: savedPaymentMethod.customerReference,
				...(meta.payUsingSavedPaymentMethod && { paymentMethodReference: savedPaymentMethod.paymentMethodReference })
			};

			// if there is stored payment method, but not for selected shop we will send this value and copy card from there
			if (!recurringPaymentDetails[shop.shopPaymentOptions.merchantBillwerkAccountId]) {
				orderDetails.recurringPaymentDetails = {...orderDetails.recurringPaymentDetails, copyCardFromAccount: !!meta.payUsingSavedPaymentMethod};
			}
		}
		// flag to ensure that only users who have the new app will have the feature
		// to save payment details. Users with old app will not have this feature...
		orderDetails.persistPaymentDetails = true;

		if (GrantPointsToNonUser.isEnabled({$store: {getters}}) && userDetails.email) {
			if (!orderDetails.orderOptions.grantPoints) {
				orderDetails.orderOptions.grantPoints = {};
			}
			orderDetails.orderOptions.grantPoints.checkIfEmailIsSunsetPoint = true;
		}
		return OrdersProvider.sendOrder(orderDetails)
			.then(response => {
				const data = response.data;
				if (data.payment.paymentType !== "zeroPayment") {
					check.string.orFail(data.payment.redirectUrl);
				}

				check.string.orFail(data.extId);

				// store order id for later reuse
				commit(actions.SET_CART_ORDER_ID, data.extId);
				commit(actions.SET_CART_ORDER_TAGS, data.orderTags);

				if (data && data.customerDetails) {
					const { email, name, telephone, emailNewsletter } = data.customerDetails;
					commit(actions.USER_DATA_SET, { email, name, tel: telephone, emailNewsletter });
				}

				// update pickupTime if ASAP
				if (state.persistedData.newCartData.pickupTime === "ASAP") {
					commit(actions.CART_SET_PICKUP_DATA, {
						pickupDate: state.persistedData.newCartData.pickupDate,
						pickupTime: pickupTime,
						submitted: state.persistedData.newCartData.pickupData.submitted
					});
				}

				return data;

			});
	},

	[actions.CART_INITIALIZE_TERMINAL_PAYMENT]: ({ state, commit, getters }) => {
		const employeeDiscountNumberObject = getters.discount ? { employeeDiscountNumber: getters.discount.promoCode } : {};
		const userDetails = getters.getUserData;
		const customerDetails = {
			name: userDetails.name,
			email: userDetails.email,
			telephone: userDetails.tel,
			...employeeDiscountNumberObject,
			employeeDiscountToken: getters.employeeDiscountToken && getters.employeeDiscountToken.token
				? getters.employeeDiscountToken
				: undefined,
			customerType: evaluateCustomerType()
		};

		const shopId = (() => {
			// if shop has shop data in vuex take it from there
			const shopIdStore = _.get(state, "persistedData.newCartData.shopData.id");
			if (shopIdStore) {
				return shopIdStore;
			}

			// if shop has global data then take id from there
			const shopIdGlobal = _.get(getters, "globalSelectedShop.id");
			if (shopIdGlobal) {
				return shopIdGlobal;
			}

			// else throw error
			throw AppError.createNewError("ShopDataNotFound");
		})();
		const now = getters.ShopTimeBoxHelper.nowInTimeZone();
		const asapWaitingTime = getters.tenantSettings.asapWaitingTime;
		const isCustomClosingTimeEnabled = getters.featureToggles.useCustomClosingTimeOffset;
		const customAsapTime = getters.tenantSettings.customAsapTime;
		const applyCustomAsapTime = shouldApplyCustomAsapTime(getters, asapWaitingTime);
		const pickupDate = now.add({
			minutes: isCustomClosingTimeEnabled && applyCustomAsapTime ? customAsapTime : asapWaitingTime.minutes
		}).format(TimeBoxFormats.YYYY_MM_DD).exec();
		const pickupTime = now.add({
			minutes: isCustomClosingTimeEnabled && applyCustomAsapTime ? customAsapTime : asapWaitingTime.minutes
		}).format(TimeBoxFormats.HH_mm).exec();

		const orderDetails = {
			cartOrderId: state.persistedData.newCartData.cartOrderId,
			shopId: shopId,
			pickupDate: pickupDate,
			pickupTime: pickupTime,
			productSelections: state.persistedData.newCartData.productSelections,
			customerDetails: customerDetails,
			subscriptionEnabled: getters.applicationUserLoggedIn && getters.featureToggles.enableSubscription,
			orderOptions: {
				mustBeHeated: state.persistedData.newCartData.mustBeHeated,
				...state.persistedData.newCartData.tenantSpecificData,
				sunsetPoints: getters.sunsetPoints,
				enableProductBestPriceInCatalogAndCart: getters.featureToggles.enableProductBestPriceInCatalogAndCart,
				...(getters.channelAlias === "kiosk" && { shopDeviceIdentifier: (window.kioskHost || {}).shopDeviceIdentifier})
			},
			specialNotes: state.persistedData.newCartData.specialNotes,
			userConsent: state.persistedData.newCartData.userConsent,
			isPrepaid: true,
			paymentType: "terminalPayment",
			redeemedVouchers: state.persistedData.newCartData.vouchersRedeemed,
			orderRef: getSessionKey(appConfig.clientTraceId(), getters.deviceData),
			extendedData: {fcmToken: getters.featureToggles.postPurchaseDiscount && getters.fcmToken}
		};
		if(getters.isReorderState) {
			appendReorderData(orderDetails, getters);
		}
		if(getters.voucherVirtualOrderId) {
			orderDetails.orderOptions = {...orderDetails.orderOptions, voucherVirtualOrderId: getters.voucherVirtualOrderId};
		}
		if (getters.ppdProductSelectionIds && getters.ppdProductSelectionIds.length) {
			orderDetails.orderOptions.ppdFlowSkipSendingPushNotification = true;
		}
		return OrdersProvider.sendOrder(orderDetails).then(response => {
			const data = response.data;

			check.string.orFail(data.extId);

			// store order id for later reuse
			commit(actions.SET_CART_ORDER_ID, data.extId);
			// commit(actions.SET_CART_ORDER_TAGS, data.orderTags);

			if (data && data.customerDetails) {
				const { email, name, telephone } = data.customerDetails;
				commit(actions.USER_DATA_SET, { email, name, tel: telephone });
			}

			// update pickupTime if ASAP
			if (state.persistedData.newCartData.pickupTime === "ASAP") {
				commit(actions.CART_SET_PICKUP_DATA, {
					pickupDate: state.persistedData.newCartData.pickupDate,
					pickupTime: pickupTime,
					submitted: state.persistedData.newCartData.pickupData.submitted
				});
			}
			HelloRetail.conversionTracking({$store: {getters}}, {order: data});
			return data;

		});
	},
	[actions.CART_GET_ORDER_NUMBER_BY_PAYMENT]: async({ commit, getters }, { txnId }) => {
		const payment = await PaymentProvider.getOrderNumberByPayment(txnId);
		commit(actions.CHECKOUT_ORDER_SENT, {
			orderNumber: payment.orderNumber,
			productCatalog: getters.productCatalog,
			orderIsPrepaid: true,
			featureToggles: getters.featureToggles,
			applicationUserData: getters.applicationUserData
		});
		return payment;
	},
	[actions.CART_SAVE_PAYMENT]: async({ commit, getters }, { paymentData }) => {
		const { method = "get", ...data } = paymentData;
		const payment = await PaymentProvider.savePayment(data, method);

		commit(actions.CHECKOUT_ORDER_SENT, {
			orderNumber: payment.orderNumber,
			productCatalog: getters.productCatalog,
			orderIsPrepaid: true,
			totalAmountCents: payment.totalAmountCents,
			featureToggles: getters.featureToggles,
			applicationUserData: getters.applicationUserData
		});

		const shop = getters.globalSelectedShop || {};
		const shopPaymentOptions = shop.shopPaymentOptions || {};
		const customerDetails = getters.customerDetails || {};
		let recurringPaymentDetailsData = { ...getters.recurringPaymentDetails };
		const paymentMethod = payment.paymentMethod || {};
		const maskedCard = paymentMethod.maskedCard;

		// we will filter out the recurring payment details that are not the same masked card as the payment methods for the last one (this one)
		// because only one card can be present at a time inside the system
		// next time customer choose that new card for the shop that is in different billwer account it will be copied to that account on BE
		// every time customer changes the card it will remove all payment methods with old card number and process starts from the beginning with that new card as default to be copied on demand
		recurringPaymentDetailsData = Object.fromEntries(
			Object.entries(recurringPaymentDetailsData).filter(([key, value]) => {
				if (key === "customerReference") {
					return true;
				}
				return maskedCard && value.maskedCard === maskedCard;
			})
		);

		// if we have the idp details, we will set the main customer reference which will act as a parent wrapper
		// we need this so that when logging in, we can check if the saved and logged in customers are the same.
		if (customerDetails.id) {
			recurringPaymentDetailsData.customerReference = customerDetails.id;
		}

		if (paymentData.data.customerReference && maskedCard) {
			recurringPaymentDetailsData = {
				...recurringPaymentDetailsData,
				[shopPaymentOptions.merchantBillwerkAccountId]: {
					...paymentMethod,
					customerReference: paymentData.data.customerReference // this can be the billwerk customer handle or the externalIdpId
				}
			};
		} else {
			delete recurringPaymentDetailsData[shopPaymentOptions.merchantBillwerkAccountId];
		}

		commit(actions.SET_RECURRING_PAYMENT_DETAILS, recurringPaymentDetailsData);

		return payment;
	},

	[actions.FACEBOOK_PIXEL_PURCHASE]: ({ commit }) => {
		return new Promise((resolve) => {
			commit(actions.FACEBOOK_PIXEL_PURCHASE);
			setLastUpdateTime(commit);
			resolve();
		});
	},
	[actions.ADD_ORDER_TAGS_BASED_ON_PICKUP_OPTION]: ({ commit, getters }) => {
		const orderTags = (getters.cartOrderTags || []).filter((e) => !e.startsWith("(Delivery"));
		const deliveryChoice = getters.tenantSpecificData.deliveryChoice;
		if (deliveryChoice === DeliveryMethodsEnum.DELIVERY) {
			const price = getters.deliveryDetails
				? getters.deliveryDetails.price
				: Object
					.values(
						getters.globalSelectedShop.shopOptions
							.deliveryMethods.find((e) => e.ref === "DELIVERY")
							.prices
					)
					.sort((a, b) => b - a)
					.find(Boolean);
			orderTags.push(`(Delivery, ${price})`);
		}
		commit(actions.SET_CART_ORDER_TAGS, orderTags);
	},
	[actions.SET_CART_ORDER_TAGS]: ({ commit}, { orderTags }) => {
		commit(actions.SET_CART_ORDER_TAGS, orderTags);
	},
	[actions.RECALCULATE_SUBSCRIPTION_SET_CART_ORDER_TAGS]: async ({ commit, getters }) => {
		const computedTags = await computeTags(getters.subscriptionDetails, getters.customerFetchingSuccess);
		const newOrderTags = (getters.cartOrderTags || [])
			.filter((e) => !["Membership", "ProductId", "CustomerId", "FirstMeal"].some((subTab) => e.startsWith(`(${subTab}`)))
			.concat(computedTags)
			.filter(Boolean);
		commit(actions.SET_CART_ORDER_TAGS, newOrderTags);
	},
	[actions.ADD_VOUCHER_TO_ORDER]: ({ commit, getters }, voucherData) => {
		return new Promise((resolve) => {
			const orderTags = (getters.cartOrderTags || [])
				.filter(item => !item.startsWith("(Voucher,"))
				.concat(voucherData.tags);
			commit(actions.SET_CART_ORDER_TAGS, orderTags);
			commit(actions.ADD_VOUCHER_TO_ORDER, voucherData.voucherId);
			setLastUpdateTime(commit);
			resolve();
		});
	},

	[actions.CLEAR_VOUCHERS_FROM_ORDER]: ({ commit }) => {
		return new Promise((resolve) => {
			commit(actions.CLEAR_VOUCHERS_FROM_ORDER);
			setLastUpdateTime(commit);
			resolve();
		});
	},

	[actions.CART_SUBMIT_ORDER]: ({ state, commit, getters }, { discount }) => {
		const userDetails = getters.getUserData;
		const customerDetails = {
			name: userDetails.name,
			email: userDetails.email,
			telephone: userDetails.tel,
			employeeDiscountNumber: discount.promoCode,
			employeeDiscountToken: getters.employeeDiscountToken && getters.employeeDiscountToken.token
				? getters.employeeDiscountToken
				: undefined,
			customerType: evaluateCustomerType()
		};

		// if shop has global data then take id from there, else get from vuex store
		const shopId = getters.globalSelectedShop === null ?
			state.persistedData.newCartData.shopData.id : getters.globalSelectedShop.id;
		const asapWaitingTime = getters.tenantSettings.asapWaitingTime;
		const isCustomClosingTimeEnabled = getters.featureToggles.useCustomClosingTimeOffset;
		const customAsapTime = getters.tenantSettings.customAsapTime;
		const applyCustomAsapTime = shouldApplyCustomAsapTime(getters, asapWaitingTime);
		const now = getters.ShopTimeBoxHelper.nowInTimeZone();
		const pickupTime = state.persistedData.newCartData.pickupTime === "ASAP" ?
			now
				.add({
					minutes: isCustomClosingTimeEnabled && applyCustomAsapTime ? customAsapTime : asapWaitingTime.minutes
				})
				.set({ second: asapWaitingTime.seconds })
				.format(TimeBoxFormats.HH_mm_ss)
				.exec() :
			state.persistedData.newCartData.pickupTime;
		const orderDetails = {
			cartOrderId: state.persistedData.newCartData.cartOrderId,
			shopId: shopId,
			pickupDate: state.persistedData.newCartData.pickupDate,
			pickupTime: pickupTime,
			productSelections: state.persistedData.newCartData.productSelections,
			customerDetails: customerDetails,
			orderOptions: {
				mustBeHeated: state.persistedData.newCartData.mustBeHeated,
				...state.persistedData.newCartData.tenantSpecificData,
				sunsetPoints: getters.sunsetPoints,
				...(getters.channelAlias === "kiosk" && { shopDeviceIdentifier: (window.kioskHost || {}).shopDeviceIdentifier})
			},
			specialNotes: state.persistedData.newCartData.specialNotes,
			userConsent: state.persistedData.newCartData.userConsent,
			redeemedVouchers: state.persistedData.newCartData.vouchersRedeemed,
			orderRef: getSessionKey(appConfig.clientTraceId(), getters.deviceData)
		};

		return OrdersProvider.sendOrder(orderDetails).then((response) => {

			check.string.orFail(response.data.orderNumber);
			commit(actions.CHECKOUT_ORDER_SENT, {
				orderNumber: response.data.orderNumber,
				productCatalog: getters.productCatalog,
				featureToggles: getters.featureToggles,
				applicationUserData: getters.applicationUserData
			});

			commit(actions.SET_CART_ORDER_ID, null);
			if (response && response.data && response.data.orderTags) {
				commit(actions.SET_CART_ORDER_TAGS, response.data.orderTags);
			}

			return response.data.orderNumber;
		});
	},

	[actions.GATHER_EVALUATION_TAG_DATA]: async ({ getters, commit }, payload = {}) => {
		const subscriptionDetails = getters.subscriptionDetails || {};
		const hasFreeMeal = getters.userCrmVouchersAndMilestoneRewards.filter(verifyVoucherAvailability)
			.find(v => v.options && v.options.label === "FreeMeal" && !v.usedAt);
		const data = generateEvaluateTags({
			channel: appConfig.channelAlias(),
			orderTags: getters.cartOrderTags,
			userData: {
				...getters.getUserData,
				token: getters.userIdpToken || getters.applicationUserIdpToken
			},
			subscriptionType: subscriptionDetails.subscriptionPlanName,
			hasFreeMeal: !!hasFreeMeal,
			showSunsetPointsInfo: getters.showSunsetPointsInfo,
			page: payload.page,
			platform: appConfig.mobile.mobileApi().platformId,
			errors: _.get(getters.customerDetails, "meta.subscription.errors", {})
		});
		const customerType = getCustomerType({
			subscriptionType: subscriptionDetails.subscriptionPlanName,
			showSunsetPointsInfo: getters.showSunsetPointsInfo
		});
		commit(actions.GATHER_EVALUATION_TAG_DATA, {...data, currentPage: payload.page, customerType});
	},

	[actions.GATHER_TARGET_GROUP_DATA]: async ({ commit, getters }) => {
		const targetGroupData = await TargetGroupsProvider.getAll({ isActive: true }, getters.jwtToken);
		commit(actions.STORE_TARGET_GROUP_DATA, targetGroupData);
	},

	[actions.FETCH_AND_CACHE_SHOPS]: ({ commit, getters }, options = {} ) => {
		const {
			authToken,
			onlyActive,
			forceRefresh = true,
			ignoreChannels,
			showSecretRestaurants,
			cacheShops = true
		} = options;
		const useNewShopsData = getters.featureToggles.useNewShopsData;
		if (getters.cachedShops.length && !forceRefresh) {
			return Promise.resolve(getters.cachedShops);
		} else if (ignoreChannels) {
			return ShopsProvider
				.findAll(authToken, onlyActive, useNewShopsData, showSecretRestaurants)
				.then((response => {
					if (cacheShops) {
						commit(actions.SET_CACHED_SHOPS, response.data);
						return getters.cachedShops;
					}
					return response.data;
				}));
		} else {
			return ShopsProvider
				.findAllByChannel(authToken, onlyActive, useNewShopsData, showSecretRestaurants)
				.then((response => {
					if (cacheShops) {
						commit(actions.SET_CACHED_SHOPS, response.data);
						return getters.cachedShops;
					}
					return response.data;
				}));
		}
	},

	[actions.FETCH_AND_CACHE_PICKUP_RULES]: ({ commit, getters }, { forceRefresh = false }) => {
		if (getters.cachedPickupRules.length && forceRefresh !== true) {
			return Promise.resolve(getters.cachedPickupRules);
		} else {
			return PickupDateTimeRulesProvider.findAll().then((response => {
				const activeRules = Array.isArray(response.data) ? response.data.filter(rule => rule.isActive) : response.data;
				commit(actions.SET_CACHED_PICKUP_RULES, activeRules);
				return getters.cachedPickupRules;
			}));
		}
	},

	[actions.MUST_BE_HEATED]: ({ commit }, mustBeHeatedValue) => {
		return new Promise((resolve) => {
			commit(actions.MUST_BE_HEATED, mustBeHeatedValue);
			setLastUpdateTime(commit);
			resolve();
		});
	},

	[actions.SAVE_ORDER_CHANGE]: ({ state, commit, getters }, { comment, changedBy, urlAccept, urlCancel, urlCheckout, integrationType }) => {
		const userDetails = getters.getUserData;
		const customerDetails = {
			name: userDetails.name,
			email: userDetails.email,
			telephone: userDetails.tel
		};

		return OrdersProvider.sendOrder({
			cartOrderId: state.persistedData.editModeData.cartOrderId,
			shopId: state.persistedData.editModeData.shopData.id,
			pickupDate: state.persistedData.editModeData.pickupDate,
			pickupTime: state.persistedData.editModeData.pickupTime,
			productSelections: state.persistedData.editModeData.productSelections,
			customerDetails: customerDetails,
			orderChangedBy: changedBy,
			orderChangedDescription: comment,
			isEditMode: true,
			orderOptions: {
				mustBeHeated: state.persistedData.editModeData.mustBeHeated,
				paymentLinkThroughEmail: state.persistedData.editModeData.paymentLinkThroughEmail,
				...state.persistedData.editModeData.tenantSpecificData,
				sunsetPoints: getters.sunsetPoints,
				...(getters.channelAlias === "kiosk" && { shopDeviceIdentifier: (window.kioskHost || {}).shopDeviceIdentifier})
			},
			isPrepaid: state.persistedData.editModeData.orderIsPrepaid,
			integrationType: integrationType,
			prepaidUrlAccept: urlAccept,
			prepaidUrlCancel: urlCancel,
			prepaidUrlCheckout: urlCheckout,
			specialNotes: state.persistedData.editModeData.specialNotes,
			userConsent: false,
			isAdmin: true,
			orderRef: getSessionKey(appConfig.clientTraceId(), getters.deviceData)
		}).then((response) => {
			check.string.orFail(response.data.orderNumber);

			const returnUrl = getters.orderEditType === "stepShop" ? "/admin/orders" : `/admin/orders/${getters.cartOrderId}`;

			commit(actions.DISABLE_ORDER_EDIT_MODE);
			commit(actions.DISABLE_USER_EDIT_MODE);

			return returnUrl;
		});
	},

	[actions.CANCEL_ORDER_CHANGE]: ({ commit, getters }) => {
		const orderId = getters.cartOrderId;
		commit(actions.DISABLE_ORDER_EDIT_MODE);
		commit(actions.DISABLE_USER_EDIT_MODE);

		return `/admin/orders/${orderId}`;
	},

	// discount
	[actions.SET_DISCOUNT_CODE]: ({ commit }, discount) => {
		setLastUpdateTime(commit);
		return commit(actions.SET_DISCOUNT_CODE, discount);
	},

	// remove discount
	[actions.REMOVE_DISCOUNT_CODE]: ({ commit }, discount) => {
		setLastUpdateTime(commit);
		return commit(actions.SET_DISCOUNT_CODE, { ...discount, isRemoved: true });
	}
};

const setLastUpdateTime = (commit) => {
	commit(actions.SET_LAST_UPDATE_TIME);
};
